import { Box } from '@mui/material';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { UPDropdown } from 'components/dropdown';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getExternalReferenceAction, putCompanyExternalReferences } from 'store/actions/company-actions';
import {
  companyExternalReferenceLoadingSelector,
  salaryEngineReferenceIdSelector,
} from 'store/selectors/company-selectors';
import theme from 'theme';
import {
  COMPANY_INTEGRATION__EXTERNAL_INTEGRATIONS,
  COMPANY_INTEGRATION__SALARY_ENGINE_REFERENCE,
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_AND_NEXT_BUTTON,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { ICompanyExternalReferences } from 'types/company/ICompanyExternalReferences';
import { ConfigurableExternalEnvironments, ApiEnvironment } from 'types/apiEnvironment';
import { ButtonsContainer, SectionContainer } from '../styles';
import { RowContainer } from './styles';
import StrobboIntegration from './strobbo/StrobboIntegration';
import BeepleIntegration from './beeple/BeepleIntegration';

const EditIntegration = (): JSX.Element => {
  const [t] = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const { handleSubmit } = useForm<ICompanyExternalReferences>();

  const references = useAppSelector(salaryEngineReferenceIdSelector);
  const loading = useAppSelector(companyExternalReferenceLoadingSelector);

  const [environmentKey, setEnvironmentKey] = useState<string>('');
  const [externalApiAccessKey, setExternalApiAccessKey] = useState<string>('');
  const [saveAndNextClicked, setSaveAndNextClicked] = useState<boolean>(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');

  const [description, setDescription] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [privateKey, setPrivateKey] = useState<string>('');

  const onCancel = useCallback(() => {
    navigate(`/company/${companyId}/managers`);
  }, [companyId, navigate]);

  useEffect(() => {
    dispatch(getExternalReferenceAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  useEffect(() => {
    if (references) {
      debugger; // eslint-disable-line no-debugger
      setEnvironmentKey(references.environmentKey);
      setExternalApiAccessKey(references.externalApiAccessKey);
      setDescription(references.description);
      setBaseUrl(references.baseUrl);
      setUserName(references.userName);
      setPassword(references.password);
      setPrivateKey(references.privateKey);
      setSelectedEnvironment(references.apiEnvironment?.toString());
    }
  }, [references]);

  const onSaveExternalReferences: SubmitHandler<any> = useCallback(() => {
    const data: ICompanyExternalReferences = {
      environmentKey,
      externalApiAccessKey,
      description,
      baseUrl,
      userName,
      password,
      privateKey,
      apiEnvironment: +selectedEnvironment,
    };
    dispatch(putCompanyExternalReferences({ companyId, data }));
  }, [
    companyId,
    dispatch,
    environmentKey,
    externalApiAccessKey,
    description,
    baseUrl,
    userName,
    password,
    privateKey,
    selectedEnvironment,
  ]);

  useEffect(() => {
    if (saveAndNextClicked) {
      setSaveAndNextClicked(false);
      setTimeout(() => {
        navigate(`/company/${companyId}/structure`);
      }, 1000);
    }
  }, [navigate, saveAndNextClicked, companyId, dispatch]);

  const onSaveAndNext = useCallback(() => {
    handleSubmit(onSaveExternalReferences)();
    setSaveAndNextClicked(true);
  }, [handleSubmit, onSaveExternalReferences]);

  return (
    <SectionContainer>
      <RowContainer>
        <UPInput
          value={references?.salaryEngineReferenceId}
          label={t(COMPANY_INTEGRATION__SALARY_ENGINE_REFERENCE)}
          placeholder={t(COMPANY_INTEGRATION__SALARY_ENGINE_REFERENCE)}
          helperText=""
          onChange={null}
          required
          disabled
        />
      </RowContainer>
      <RowContainer>
        <UPDropdown
          value={selectedEnvironment}
          onChange={e => setSelectedEnvironment(e.target.value)}
          placeholder={t(COMPANY_INTEGRATION__EXTERNAL_INTEGRATIONS)}
          items={ConfigurableExternalEnvironments}
          label=""
        />
      </RowContainer>
      {+selectedEnvironment === ApiEnvironment.Strobbo && (
        <StrobboIntegration
          environmentKey={environmentKey}
          externalApiAccessKey={externalApiAccessKey}
          setEnvironmentKey={setEnvironmentKey}
          setExternalApiAccessKey={setExternalApiAccessKey}
        />
      )}

      {+selectedEnvironment === ApiEnvironment.Beeple && (
        <BeepleIntegration
          environmentKey={environmentKey}
          description={description}
          baseUrl={baseUrl}
          userName={userName}
          password={password}
          privateKey={privateKey}
          setEnvironmentKey={setEnvironmentKey}
          setDescription={setDescription}
          setBaseUrl={setBaseUrl}
          setUserName={setUserName}
          setPassword={setPassword}
          setPrivateKey={setPrivateKey}
        />
      )}

      <ButtonsContainer sx={{ display: 'flex', flexDirection: 'row', mt: theme.spacing(2) }}>
        <Box sx={{ display: 'flex' }}>
          <Box>
            <UPButton
              text={t(GENERAL__SAVE_BUTTON)}
              onClick={() => handleSubmit(onSaveExternalReferences)()}
              disabled={loading}
            />
          </Box>
          <Box sx={{ marginLeft: '10px' }}>
            <UPButton text={t(GENERAL__SAVE_AND_NEXT_BUTTON)} onClick={onSaveAndNext} disabled={loading} />
          </Box>
          <Box sx={{ marginLeft: '10px' }}>
            <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onCancel} outlined />
          </Box>
        </Box>
      </ButtonsContainer>
    </SectionContainer>
  );
};

export default EditIntegration;
